"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Gift, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  MainEventDetail,
  MainEventPrizeData,
  MainEventSummary,
} from "@/services/events/main-event";

export function ordinalLabel(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function prizeValueText(prize: MainEventPrizeData): string {
  return prize.prizeType === "COINS"
    ? `${prize.coinAmount.toLocaleString()} coins`
    : prize.itemName ?? "Mystery Prize";
}

function placementTitle(placement: number): string {
  if (placement === 1) return "Champion's prize";
  if (placement === 2) return "Runner-up";
  if (placement === 3) return "Third place";
  return `${ordinalLabel(placement)} place`;
}

/** The single prize a compact surface should headline. */
export type TopPrize =
  | { type: "COINS"; coins: number }
  | { type: "ITEM"; prize: MainEventPrizeData };

/** Top prize for events-list summaries (firstPlacePrize/itemPrizes shape). */
type TopPrizeSummaryInput = Pick<
  MainEventSummary,
  "firstPlacePrize" | "itemPrizes" | "prizePool" | "prizeType"
>;

export function getTopPrize(event: TopPrizeSummaryInput): TopPrize | null {
  if (
    event.firstPlacePrize?.prizeType === "COINS"
    && event.firstPlacePrize.coinAmount > 0
  ) {
    return { type: "COINS", coins: event.firstPlacePrize.coinAmount };
  }
  const itemPrizes = event.itemPrizes ?? [];
  if (itemPrizes.length > 0) return { type: "ITEM", prize: itemPrizes[0] };
  if (event.prizePool > 0 && event.prizeType.toUpperCase() === "COINS") {
    return { type: "COINS", coins: event.prizePool };
  }
  return null;
}

/** Top prize for the event detail page (full prizes[] shape). */
export function getTopPrizeDetail(event: MainEventDetail): TopPrize | null {
  const sorted = [...(event.prizes ?? [])].sort((a, b) => a.placement - b.placement);
  const first = sorted[0];
  if (first?.prizeType === "ITEM") return { type: "ITEM", prize: first };
  if (first?.prizeType === "COINS" && first.coinAmount > 0) {
    return { type: "COINS", coins: first.coinAmount };
  }
  if (event.prizePool > 0 && event.prizeType.toUpperCase() === "COINS") {
    return { type: "COINS", coins: event.prizePool };
  }
  return null;
}

export function PrizeArtwork({
  prize,
  coinIconSrc,
  className,
  iconClassName,
}: {
  prize: MainEventPrizeData;
  coinIconSrc: string;
  className?: string;
  iconClassName?: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const hasItemImage =
    prize.prizeType === "ITEM" && prize.itemImageUrl && !imageFailed;

  return (
    <div
      className={cn(
        "relative isolate flex items-center justify-center overflow-hidden bg-surface-3/55",
        className,
      )}
    >
      <div className="absolute inset-x-5 bottom-3 h-px bg-gold/25" />
      {hasItemImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={prize.itemImageUrl ?? ""}
          alt={prize.itemName ?? "Prize"}
          className="relative z-10 h-full w-full object-contain p-2"
          onError={() => setImageFailed(true)}
        />
      ) : prize.prizeType === "COINS" ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={coinIconSrc}
          alt="Coins"
          className="relative z-10 h-1/2 w-1/2 object-contain"
        />
      ) : (
        <Gift
          className={cn(
            "relative z-10 text-gold/80",
            iconClassName ?? "h-8 w-8",
          )}
        />
      )}
    </div>
  );
}

/**
 * Compact top-prize badge — label + glyph (coins icon or item art). The small
 * graphic used on event cards; sized to sit in a tile corner.
 */
export function TopPrizeBadge({
  top,
  coinIconSrc,
  className,
}: {
  top: TopPrize;
  coinIconSrc: string;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="min-w-0 text-right">
        <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-tertiary">
          Top prize
        </p>
        <p className="max-w-[110px] truncate font-mono text-[11px] font-bold text-gold">
          {top.type === "COINS"
            ? `${top.coins.toLocaleString()} coins`
            : top.prize.itemName ?? "Mystery Prize"}
        </p>
      </div>
      {top.type === "ITEM" ? (
        <PrizeArtwork
          prize={top.prize}
          coinIconSrc={coinIconSrc}
          className="h-12 w-12 shrink-0 rounded-lg border border-gold/45 bg-surface/90"
          iconClassName="h-6 w-6"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gold/45 bg-surface/90">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={coinIconSrc} alt="Coins" className="h-7 w-7 object-contain" />
        </div>
      )}
    </div>
  );
}

export function PrizesShowcase({
  event,
  coinIconSrc,
}: {
  event: MainEventDetail;
  coinIconSrc: string;
}) {
  const reduceMotion = useReducedMotion();
  const sorted = [...(event.prizes ?? [])].sort(
    (a, b) => a.placement - b.placement,
  );
  const hasStructured = sorted.length > 0;
  const distEntries =
    !hasStructured && event.prizeDistribution
      ? Object.entries(event.prizeDistribution).sort(
          ([a], [b]) => parseInt(a) - parseInt(b),
        )
      : [];
  const legacyEntries =
    distEntries.length > 0
      ? distEntries
      : event.prizePool > 0
        ? [["1", event.prizePool] as [string, number]]
        : [];

  if (!hasStructured && distEntries.length === 0 && event.prizePool <= 0) {
    return null;
  }

  const top = sorted.slice(0, 3);
  const rest = sorted.slice(3);

  return (
    <div className="mb-8">
      {/* A trophy, not an arrow in a ring. The old mark was a circled
          ArrowRight — visually a button, actually a span — so the most
          clickable-looking thing on the sheet was the one thing that did
          nothing. The banner states the path; the sheet's Enter button is
          what walks it. */}
      <div className="mb-3 flex items-center gap-3 rounded-xl border border-verdigris/30 bg-verdigris/10 px-4 py-3 sm:px-5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center text-verdigris">
          <Trophy className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-verdigris">
            Your path to the podium
          </p>
          <p className="text-sm font-semibold text-cream">
            Pick your winners and make predictions for a chance to win.
          </p>
        </div>
      </div>

      <section className="overflow-hidden rounded-2xl border border-surface-4 bg-surface-2 shadow-lg">
        <div className="flex flex-col gap-2 border-b border-surface-4 px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-6">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.28em] text-gold">
              The prize cabinet
            </p>
            <h2 className="mt-1 font-serif text-xl font-bold text-cream sm:text-2xl">
              Rewards worth competing for
            </h2>
          </div>
          {event.prizePool > 0 && (
            <div className="sm:text-right">
              <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-tertiary">
                Total pool
              </p>
              <p className="font-mono text-sm font-bold text-gold">
                {event.prizePool.toLocaleString()} {event.prizeType.toLowerCase()}
              </p>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-5">
          {hasStructured ? (
            <>
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)]">
                {top[0] && (
                  <motion.article
                    initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={reduceMotion ? undefined : { y: -4 }}
                    className="group relative overflow-hidden rounded-xl border border-gold/45 bg-surface p-3 shadow-md sm:p-4"
                  >
                    <div className="absolute right-3 top-3 z-20 rounded-full bg-gold px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-surface">
                      {ordinalLabel(top[0].placement)}
                    </div>
                    <PrizeArtwork
                      prize={top[0]}
                      coinIconSrc={coinIconSrc}
                      className="h-48 rounded-lg border border-surface-4 sm:h-64"
                      iconClassName="h-12 w-12"
                    />
                    <div className="px-1 pb-1 pt-4">
                      <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-gold">
                        {placementTitle(top[0].placement)}
                      </p>
                      <p className="mt-1 font-serif text-xl font-bold leading-tight text-cream sm:text-2xl">
                        {prizeValueText(top[0])}
                      </p>
                    </div>
                  </motion.article>
                )}

                <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
                  {top.slice(1).map((prize, index) => (
                    <motion.article
                      key={prize.id}
                      initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.25 }}
                      transition={{
                        duration: 0.45,
                        delay: reduceMotion ? 0 : (index + 1) * 0.08,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      whileHover={reduceMotion ? undefined : { y: -3 }}
                      className="group min-w-0 rounded-xl border border-surface-4 bg-surface p-2.5 shadow-sm sm:p-3 lg:grid lg:grid-cols-[112px_minmax(0,1fr)] lg:items-center lg:gap-4"
                    >
                      <div className="relative">
                        <PrizeArtwork
                          prize={prize}
                          coinIconSrc={coinIconSrc}
                          className="h-28 rounded-lg border border-surface-4 sm:h-36 lg:h-28"
                          iconClassName="h-8 w-8"
                        />
                        <span className="absolute right-2 top-2 rounded-full border border-gold/40 bg-surface/90 px-2 py-1 font-mono text-[9px] font-bold text-gold">
                          {ordinalLabel(prize.placement)}
                        </span>
                      </div>
                      <div className="min-w-0 px-0.5 pb-1 pt-3 lg:p-0">
                        <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-tertiary">
                          {placementTitle(prize.placement)}
                        </p>
                        <p className="mt-1 line-clamp-2 text-xs font-semibold leading-tight text-cream sm:text-sm">
                          {prizeValueText(prize)}
                        </p>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </div>

              {rest.length > 0 && (
                // subgrid: the icon and amount columns are sized once for the
                // whole list, so the icons stack in a straight column whatever
                // width each row's amount takes
                <div className="mt-4 grid grid-cols-[minmax(max-content,1fr)_auto_auto] gap-x-3 overflow-hidden rounded-xl border border-surface-4 bg-surface">
                  {rest.map((prize) => (
                    <div
                      key={prize.id}
                      className="col-span-3 grid grid-cols-subgrid items-center border-t border-surface-4 px-4 py-3 first:border-t-0"
                    >
                      <span className="font-mono text-xs font-semibold text-warm-gray">
                        {ordinalLabel(prize.placement)} place
                      </span>
                      <PrizeArtwork
                        prize={prize}
                        coinIconSrc={coinIconSrc}
                        className="h-8 w-8 rounded border border-surface-4"
                        iconClassName="h-4 w-4"
                      />
                      <span
                        className={cn(
                          "min-w-0 truncate text-right text-sm font-semibold",
                          prize.prizeType === "COINS"
                            ? "font-mono text-gold"
                            : "text-cream",
                        )}
                      >
                        {prizeValueText(prize)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-[minmax(max-content,1fr)_auto_auto] gap-x-3 overflow-hidden rounded-xl border border-surface-4 bg-surface">
              {legacyEntries.map(([rank, amount]) => (
                <div
                  key={rank}
                  className="col-span-3 grid grid-cols-subgrid items-center border-t border-surface-4 px-4 py-3 first:border-t-0"
                >
                  <span className="text-sm font-medium text-warm-gray">
                    {rank.includes("-")
                      ? `${rank} place`
                      : `${ordinalLabel(parseInt(rank))} place`}
                  </span>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={coinIconSrc} alt="" className="h-5 w-5 object-contain" />
                  <span className="text-right font-mono text-sm font-bold text-gold">
                    {amount.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
